import React from 'react';
import { Link } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StarRateIcon from '@mui/icons-material/StarRate';
import VerifiedIcon from '@mui/icons-material/Verified';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';

import '../../css/home.css';
import '../../css/shopcard.css';

const defaultBanner = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80";

/* Deterministic colour from shop name — gives every shop a unique accent
   without needing a DB field. Falls back to brand maroon. */
const PALETTE = [
    ['#a85050', '#8d4040'],
    ['#2563eb', '#1d4ed8'],
    ['#059669', '#047857'],
    ['#7c3aed', '#6d28d9'],
    ['#d97706', '#b45309'],
    ['#db2777', '#be185d'],
    ['#0891b2', '#0e7490'],
];

function accentFor(name = '') {
    const idx = name.charCodeAt(0) % PALETTE.length;
    return PALETTE[idx];
}

/* ---------- Standard card (compact or normal) ----------------------------- */
function ShopCard({ shop, compact = false, joinedText, featured = false }) {
    if (!shop) return null;

    const {
        name = 'Local Store',
        slug = 'store',
        category,
        logo,
        banner,
        address,
        distanceFormatted,
        ratings,
        numReviews,
        productCount = 0,
        isOpen,
        isVerified = false,
    } = shop;

    const hasRating   = Number(numReviews) > 0 && Number(ratings) > 0;
    const knowsOpen   = typeof isOpen === 'boolean';
    const [accent1, accent2] = accentFor(name);
    const bannerSrc   = banner?.imageurl || defaultBanner;
    const initial     = name.charAt(0).toUpperCase();

    if (featured) {
        return <FeaturedShopCard shop={shop} />;
    }

    return (
        <Link
            to={`/shop/${slug}`}
            className={`sc-card${compact ? ' sc-card--compact' : ''}`}
            aria-label={`Visit ${name}`}
        >
            {/* ── Banner ── */}
            <div className="sc-banner">
                <img
                    src={bannerSrc}
                    alt={name}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="sc-banner__img"
                />
                {/* gradient scrim */}
                <div className="sc-banner__scrim" />

                {/* Accent glow on hover */}
                <div
                    className="sc-banner__glow"
                    style={{ background: `radial-gradient(ellipse at 50% 100%, ${accent1}55 0%, transparent 70%)` }}
                />

                {/* Open / Closed */}
                {knowsOpen && (
                    <span className={`sc-badge sc-badge--status sc-badge--${isOpen ? 'open' : 'closed'}`}>
                        <span className="sc-badge__dot" />
                        {isOpen ? 'Open' : 'Closed'}
                    </span>
                )}

                {/* Distance */}
                {distanceFormatted && (
                    <span className="sc-badge sc-badge--dist">
                        <LocationOnOutlinedIcon style={{ fontSize: '12px', color: '#f87171' }} />
                        {distanceFormatted}
                    </span>
                )}

                {/* Logo avatar overlapping banner bottom */}
                <div
                    className="sc-avatar"
                    style={{ background: logo?.imageurl ? '#fff' : `linear-gradient(135deg, ${accent1}, ${accent2})` }}
                >
                    {logo?.imageurl
                        ? <img src={logo.imageurl} alt={name} className="sc-avatar__img" />
                        : <span className="sc-avatar__initial">{initial}</span>
                    }
                </div>
            </div>

            {/* ── Body ── */}
            <div className="sc-body">
                {/* New badge */}
                {joinedText && (
                    <span className="sc-new-badge">
                        <FiberNewOutlinedIcon style={{ fontSize: '13px' }} />
                        {joinedText}
                    </span>
                )}

                {/* Name + verified */}
                <div className="sc-name-row">
                    <h3 className="sc-name">{name}</h3>
                    {isVerified && (
                        <VerifiedIcon style={{ fontSize: '16px', color: '#2563eb', flexShrink: 0 }} titleAccess="Verified" />
                    )}
                </div>

                {/* Category */}
                {category && <span className="sc-category">{category}</span>}

                {/* Address */}
                <div className="sc-address">
                    <LocationOnOutlinedIcon style={{ fontSize: '13px', color: '#94a3b8', flexShrink: 0 }} />
                    <span>
                        {address?.street
                            ? `${address.street}${address.city ? `, ${address.city}` : ''}`
                            : address?.city || 'Local Storefront'}
                    </span>
                </div>

                {/* Stats strip */}
                <div className="sc-stats">
                    {hasRating ? (
                        <span className="sc-stat sc-stat--rating">
                            <StarRateIcon style={{ fontSize: '14px' }} />
                            <strong>{ratings}</strong>
                            <span className="sc-stat__muted">({numReviews})</span>
                        </span>
                    ) : (
                        <span className="sc-stat sc-stat--new">New shop</span>
                    )}
                    <span className="sc-stat sc-stat--products">
                        <InventoryOutlinedIcon style={{ fontSize: '13px' }} />
                        {productCount} {Number(productCount) === 1 ? 'item' : 'items'}
                    </span>
                </div>

                {/* CTA */}
                <span className="sc-cta" style={{ background: `linear-gradient(135deg, ${accent1}, ${accent2})` }}>
                    Visit Shop <ArrowForwardIcon style={{ fontSize: '14px' }} />
                </span>
            </div>
        </Link>
    );
}

/* ---------- Featured (first) card — wider spotlight layout ---------------- */
function FeaturedShopCard({ shop }) {
    const {
        name = 'Local Store',
        slug = 'store',
        category,
        logo,
        banner,
        address,
        distanceFormatted,
        ratings,
        numReviews,
        productCount = 0,
        isOpen,
        isVerified = false,
    } = shop;

    const hasRating   = Number(numReviews) > 0 && Number(ratings) > 0;
    const knowsOpen   = typeof isOpen === 'boolean';
    const [accent1, accent2] = accentFor(name);
    const bannerSrc   = banner?.imageurl || defaultBanner;
    const initial     = name.charAt(0).toUpperCase();

    return (
        <Link to={`/shop/${slug}`} className="sc-featured" aria-label={`Featured: ${name}`}>
            {/* Full-bleed banner */}
            <div className="sc-featured__media">
                <img src={bannerSrc} alt={name} loading="lazy" decoding="async" referrerPolicy="no-referrer" />
                <div className="sc-featured__scrim" />
                <div
                    className="sc-featured__glow"
                    style={{ background: `radial-gradient(ellipse at 30% 120%, ${accent1}66 0%, transparent 60%)` }}
                />

                {/* "Featured" chip */}
                <span className="sc-feat-chip">⭐ Featured Store</span>

                {/* Distance */}
                {distanceFormatted && (
                    <span className="sc-badge sc-badge--dist">
                        <LocationOnOutlinedIcon style={{ fontSize: '12px', color: '#f87171' }} />
                        {distanceFormatted}
                    </span>
                )}

                {/* Overlay content (name + rating) */}
                <div className="sc-featured__overlay">
                    {/* Logo */}
                    <div
                        className="sc-featured__avatar"
                        style={{ background: logo?.imageurl ? '#fff' : `linear-gradient(135deg, ${accent1}, ${accent2})` }}
                    >
                        {logo?.imageurl
                            ? <img src={logo.imageurl} alt={name} />
                            : <span>{initial}</span>
                        }
                    </div>

                    <div className="sc-featured__info">
                        <div className="sc-featured__name-row">
                            <h3 className="sc-featured__name">{name}</h3>
                            {isVerified && (
                                <VerifiedIcon style={{ fontSize: '18px', color: '#60a5fa', flexShrink: 0 }} titleAccess="Verified" />
                            )}
                            {knowsOpen && (
                                <span className={`sc-badge sc-badge--status sc-badge--${isOpen ? 'open' : 'closed'}`}>
                                    <span className="sc-badge__dot" />
                                    {isOpen ? 'Open' : 'Closed'}
                                </span>
                            )}
                        </div>

                        {category && <span className="sc-featured__cat">{category}</span>}

                        <div className="sc-featured__meta">
                            {hasRating && (
                                <span className="sc-feat-stat">
                                    <StarRateIcon style={{ fontSize: '14px', color: '#fbbf24' }} />
                                    <strong>{ratings}</strong>
                                    <span style={{ opacity: 0.7 }}>({numReviews})</span>
                                </span>
                            )}
                            <span className="sc-feat-stat">
                                <InventoryOutlinedIcon style={{ fontSize: '13px' }} />
                                {productCount} {Number(productCount) === 1 ? 'item' : 'items'}
                            </span>
                            {address && (
                                <span className="sc-feat-stat">
                                    <LocationOnOutlinedIcon style={{ fontSize: '13px' }} />
                                    {address.city || address.street || 'Local'}
                                </span>
                            )}
                        </div>
                    </div>

                    <span
                        className="sc-featured__cta"
                        style={{ background: `linear-gradient(135deg, ${accent1}, ${accent2})` }}
                    >
                        Explore <ArrowForwardIcon style={{ fontSize: '16px' }} />
                    </span>
                </div>
            </div>
        </Link>
    );
}

export default ShopCard;
